/**
 * Control Surface Panel — UI for managing control surfaces, hinges, and deflection sweeps.
 *
 * This panel is used within the Thread project workspace to:
 * 1. View the assembly's bodies (main + control surfaces + auxiliary)
 * 2. Relabel bodies (e.g., mark an auxiliary body as a control surface)
 * 3. Define hinge axes for each control surface
 * 4. Create deflection sweeps and submit to Flow360
 */

import { useState } from "react";
import type { Assembly, GeometryBody, HingeSpec } from "../api/geometry";

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function BodyCard({
  body,
  onRelabel,
  onEditHinge,
  onCutAndPreview,
}: {
  body: GeometryBody;
  onRelabel: (name: string, role: string, csType?: string) => void;
  onEditHinge: (name: string) => void;
  onCutAndPreview?: (name: string) => void;
}) {
  const roleColors: Record<string, string> = {
    main: "var(--green-9)",
    control_surface: "var(--blue-9)",
    auxiliary: "var(--gray-9)",
  };

  return (
    <div
      style={{
        border: "1px solid var(--gray-6)",
        borderRadius: 8,
        padding: 12,
        marginBottom: 8,
        borderLeft: `4px solid ${roleColors[body.role] ?? "var(--gray-6)"}`,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <strong>{body.name}</strong>
          <span
            style={{
              marginLeft: 8,
              fontSize: 12,
              padding: "2px 6px",
              borderRadius: 4,
              background: "var(--gray-3)",
              color: "var(--gray-11)",
            }}
          >
            {body.role}
          </span>
          {body.cs_type && (
            <span
              style={{
                marginLeft: 4,
                fontSize: 12,
                padding: "2px 6px",
                borderRadius: 4,
                background: "var(--blue-3)",
                color: "var(--blue-11)",
              }}
            >
              {body.cs_type}
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {body.role === "auxiliary" && (
            <button
              onClick={() => onRelabel(body.name, "control_surface")}
              style={{ fontSize: 12, cursor: "pointer" }}
            >
              Mark as CS
            </button>
          )}
          {body.role === "control_surface" && (
            <>
              <button
                onClick={() => onEditHinge(body.name)}
                style={{ fontSize: 12, cursor: "pointer" }}
              >
                {body.hinge ? "Edit Hinge" : "Define Hinge"}
              </button>
              {body.hinge && onCutAndPreview && (
                <button
                  onClick={() => onCutAndPreview(body.name)}
                  style={{
                    fontSize: 12,
                    cursor: "pointer",
                    background: "var(--blue-3)",
                    color: "var(--blue-11)",
                    border: "1px solid var(--blue-6)",
                    borderRadius: 4,
                    padding: "2px 8px",
                  }}
                >
                  Cut & Preview
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {body.hinge && (
        <div style={{ fontSize: 12, color: "var(--gray-10)", marginTop: 4 }}>
          Hinge: origin=[{body.hinge.origin.map((v) => v.toFixed(2)).join(", ")}
          ] dir=[{body.hinge.direction.map((v) => v.toFixed(2)).join(", ")}]
          <br />
          Range: [{body.hinge.min_deflection_deg}, {body.hinge.max_deflection_deg}]deg
        </div>
      )}

      <div style={{ fontSize: 11, color: "var(--gray-9)", marginTop: 4 }}>
        {body.format.toUpperCase()} | {body.face_count} faces |{" "}
        {body.is_solid ? "solid" : "sheet"}
      </div>
    </div>
  );
}

function HingeEditor({
  surfaceName,
  currentHinge,
  onSave,
  onCancel,
}: {
  surfaceName: string;
  currentHinge?: HingeSpec;
  onSave: (hinge: HingeSpec) => void;
  onCancel: () => void;
}) {
  const [hinge, setHinge] = useState<HingeSpec>(
    currentHinge ?? {
      origin: [0, 0, 0],
      direction: [0, 1, 0],
      min_deflection_deg: -30,
      max_deflection_deg: 30,
    },
  );

  const updateOrigin = (idx: number, val: number) => {
    const origin = [...hinge.origin] as [number, number, number];
    origin[idx] = val;
    setHinge({ ...hinge, origin });
  };

  const updateDirection = (idx: number, val: number) => {
    const direction = [...hinge.direction] as [number, number, number];
    direction[idx] = val;
    setHinge({ ...hinge, direction });
  };

  const labels = ["X", "Y", "Z"];

  return (
    <div
      style={{
        border: "1px solid var(--blue-6)",
        borderRadius: 8,
        padding: 12,
        marginBottom: 12,
        background: "var(--blue-2)",
      }}
    >
      <strong>Edit Hinge: {surfaceName}</strong>

      <div style={{ marginTop: 8 }}>
        <label style={{ fontSize: 12 }}>Origin (x, y, z):</label>
        <div style={{ display: "flex", gap: 4 }}>
          {labels.map((l, i) => (
            <input
              key={l}
              type="number"
              step="0.1"
              value={hinge.origin[i]}
              onChange={(e) => updateOrigin(i, parseFloat(e.target.value) || 0)}
              style={{ width: 70, fontSize: 12 }}
              placeholder={l}
            />
          ))}
        </div>
      </div>

      <div style={{ marginTop: 8 }}>
        <label style={{ fontSize: 12 }}>Direction (dx, dy, dz):</label>
        <div style={{ display: "flex", gap: 4 }}>
          {labels.map((l, i) => (
            <input
              key={l}
              type="number"
              step="0.1"
              value={hinge.direction[i]}
              onChange={(e) =>
                updateDirection(i, parseFloat(e.target.value) || 0)
              }
              style={{ width: 70, fontSize: 12 }}
              placeholder={`d${l}`}
            />
          ))}
        </div>
      </div>

      <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
        <div>
          <label style={{ fontSize: 12 }}>Min deg:</label>
          <input
            type="number"
            value={hinge.min_deflection_deg}
            onChange={(e) =>
              setHinge({
                ...hinge,
                min_deflection_deg: parseFloat(e.target.value) || -30,
              })
            }
            style={{ width: 60, fontSize: 12, marginLeft: 4 }}
          />
        </div>
        <div>
          <label style={{ fontSize: 12 }}>Max deg:</label>
          <input
            type="number"
            value={hinge.max_deflection_deg}
            onChange={(e) =>
              setHinge({
                ...hinge,
                max_deflection_deg: parseFloat(e.target.value) || 30,
              })
            }
            style={{ width: 60, fontSize: 12, marginLeft: 4 }}
          />
        </div>
      </div>

      <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
        <button onClick={() => onSave(hinge)} style={{ cursor: "pointer" }}>
          Save
        </button>
        <button onClick={onCancel} style={{ cursor: "pointer" }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function DeflectionSweepForm({
  controlSurfaces,
  onCreateSweep,
}: {
  controlSurfaces: GeometryBody[];
  onCreateSweep: (
    surfaces: Record<string, number[]>,
    mode: "full_factorial" | "one_at_a_time" | "paired",
  ) => void;
}) {
  const [mode, setMode] = useState<
    "full_factorial" | "one_at_a_time" | "paired"
  >("full_factorial");
  const [surfaceAngles, setSurfaceAngles] = useState<
    Record<string, string>
  >(() => {
    const initial: Record<string, string> = {};
    for (const cs of controlSurfaces) {
      initial[cs.name] = "-20, -10, 0, 10, 20";
    }
    return initial;
  });

  const handleSubmit = () => {
    const surfaces: Record<string, number[]> = {};
    for (const [name, anglesStr] of Object.entries(surfaceAngles)) {
      surfaces[name] = anglesStr
        .split(",")
        .map((s) => parseFloat(s.trim()))
        .filter((n) => !isNaN(n));
    }
    onCreateSweep(surfaces, mode);
  };

  return (
    <div
      style={{
        border: "1px solid var(--gray-6)",
        borderRadius: 8,
        padding: 12,
        marginTop: 12,
      }}
    >
      <strong>Deflection Sweep</strong>

      <div style={{ marginTop: 8 }}>
        <label style={{ fontSize: 12 }}>Mode: </label>
        <select
          value={mode}
          onChange={(e) => setMode(e.target.value as typeof mode)}
          style={{ fontSize: 12 }}
        >
          <option value="full_factorial">Full Factorial</option>
          <option value="one_at_a_time">One at a Time</option>
          <option value="paired">Paired</option>
        </select>
      </div>

      {controlSurfaces.map((cs) => (
        <div key={cs.name} style={{ marginTop: 8 }}>
          <label style={{ fontSize: 12 }}>
            {cs.name} ({cs.cs_type ?? "custom"}):
          </label>
          <input
            type="text"
            value={surfaceAngles[cs.name] ?? ""}
            onChange={(e) =>
              setSurfaceAngles((prev) => ({
                ...prev,
                [cs.name]: e.target.value,
              }))
            }
            style={{ width: "100%", fontSize: 12, marginTop: 2 }}
            placeholder="e.g. -20, -10, 0, 10, 20"
          />
        </div>
      ))}

      <button
        onClick={handleSubmit}
        style={{ marginTop: 12, cursor: "pointer" }}
      >
        Create Sweep
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Panel
// ---------------------------------------------------------------------------

interface ControlSurfacePanelProps {
  assembly: Assembly;
  onUpdateHinge: (surfaceName: string, hinge: HingeSpec) => void;
  onRelabelBody: (name: string, role: string, csType?: string) => void;
  onCreateSweep: (
    surfaces: Record<string, number[]>,
    mode: "full_factorial" | "one_at_a_time" | "paired",
  ) => void;
  onCutAndPreview?: (csName: string) => void;
  onUploadToFlow360?: () => void;
  sweep: { case_count: number; cases: Array<{ deflections: Record<string, number> }> } | null;
  loading?: boolean;
}

export default function ControlSurfacePanel({
  assembly,
  onUpdateHinge,
  onRelabelBody,
  onCreateSweep,
  onCutAndPreview,
  onUploadToFlow360,
  sweep,
  loading,
}: ControlSurfacePanelProps) {
  const [editingHinge, setEditingHinge] = useState<string | null>(null);

  const mainBody = assembly.bodies.find((b) => b.role === "main");
  const controlSurfaces = assembly.bodies.filter(
    (b) => b.role === "control_surface",
  );
  const auxiliaries = assembly.bodies.filter((b) => b.role === "auxiliary");

  const editingBody = editingHinge
    ? assembly.bodies.find((b) => b.name === editingHinge)
    : null;

  return (
    <div style={{ padding: 16, maxWidth: 480 }}>
      <h3 style={{ marginBottom: 4 }}>
        {assembly.name}
        <span
          style={{
            fontSize: 12,
            marginLeft: 8,
            color: "var(--gray-9)",
          }}
        >
          {assembly.source === "openvsp" ? "OpenVSP" : "Imported"}
        </span>
      </h3>

      {loading && (
        <div style={{ color: "var(--blue-9)", marginBottom: 8 }}>
          Processing...
        </div>
      )}

      {/* Main Body */}
      {mainBody && (
        <div style={{ marginBottom: 12 }}>
          <h4 style={{ fontSize: 13, color: "var(--gray-10)", marginBottom: 4 }}>
            Main Body
          </h4>
          <BodyCard
            body={mainBody}
            onRelabel={onRelabelBody}
            onEditHinge={() => {}}
          />
        </div>
      )}

      {/* Control Surfaces */}
      {controlSurfaces.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <h4 style={{ fontSize: 13, color: "var(--gray-10)", marginBottom: 4 }}>
            Control Surfaces ({controlSurfaces.length})
          </h4>
          {controlSurfaces.map((cs) => (
            <BodyCard
              key={cs.id}
              body={cs}
              onRelabel={onRelabelBody}
              onEditHinge={setEditingHinge}
              onCutAndPreview={onCutAndPreview}
            />
          ))}
        </div>
      )}

      {/* Hinge Editor */}
      {editingHinge && editingBody && (
        <HingeEditor
          surfaceName={editingHinge}
          currentHinge={editingBody.hinge}
          onSave={(hinge) => {
            onUpdateHinge(editingHinge, hinge);
            setEditingHinge(null);
          }}
          onCancel={() => setEditingHinge(null)}
        />
      )}

      {/* Auxiliaries */}
      {auxiliaries.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <h4 style={{ fontSize: 13, color: "var(--gray-10)", marginBottom: 4 }}>
            Other Bodies ({auxiliaries.length})
          </h4>
          {auxiliaries.map((body) => (
            <BodyCard
              key={body.id}
              body={body}
              onRelabel={onRelabelBody}
              onEditHinge={() => {}}
            />
          ))}
        </div>
      )}

      {/* Deflection Sweep */}
      {controlSurfaces.length > 0 && (
        <DeflectionSweepForm
          controlSurfaces={controlSurfaces}
          onCreateSweep={onCreateSweep}
        />
      )}

      {/* Sweep Results */}
      {sweep && (
        <div
          style={{
            marginTop: 12,
            padding: 12,
            background: "var(--green-2)",
            borderRadius: 8,
            fontSize: 13,
          }}
        >
          <strong>Sweep: {sweep.case_count} cases</strong>
          <div style={{ marginTop: 4, fontSize: 12, maxHeight: 120, overflow: "auto" }}>
            {sweep.cases.slice(0, 10).map((c, i) => (
              <div key={i} style={{ color: "var(--gray-10)" }}>
                {Object.entries(c.deflections)
                  .map(([k, v]) => `${k}=${v > 0 ? "+" : ""}${v}`)
                  .join(", ")}
              </div>
            ))}
            {sweep.case_count > 10 && (
              <div style={{ color: "var(--gray-8)" }}>
                ... and {sweep.case_count - 10} more
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upload to Flow360 */}
      {onUploadToFlow360 && (
        <button
          onClick={onUploadToFlow360}
          disabled={loading}
          style={{
            marginTop: 12,
            width: "100%",
            padding: 10,
            cursor: loading ? "wait" : "pointer",
            background: "var(--blue-9)",
            color: "white",
            border: "none",
            borderRadius: 6,
            fontSize: 13,
          }}
        >
          Upload to Flow360
        </button>
      )}
    </div>
  );
}
